import { action, computed, observable } from 'mobx';
import { setter, toggle } from 'mobx-decorators';
import { deserializeWithMappers, setFieldValues } from 'stores/Utils';
import WidgetConfigStore from './WidgetConfig/WidgetConfigStore';
import RuleStore from './Activity/Rule/RuleStore';
import RewardStore from './Reward/RewardStore';
import MemberStore from './Member/MemberStore';
import constants from 'Constants';
import * as utils from 'Utils';


class PageStore {
  NAV_STORE_REWARDS = "store-rewards";
  NAV_EARNING = "earning-points";
  NAV_ACTIVITIES = "activities";

  @setter @observable id;
  @setter @observable platform;
  @setter @observable widgetConfig;
  @setter @observable activityRules;
  @setter @observable rewards;
  @setter @observable member;
  @setter @observable cssStyles;

  @setter @observable view;
  @setter @observable nav;
  @setter @observable el;
  @setter @observable memberExternalId;
  @setter @observable onReadyCallback;
  @setter @observable error;

  constructor() {
    this.reset();
  }

  @action reset() {
    this.id = null;
    this.platform = "";
    this.widgetConfig = new WidgetConfigStore();
    this.activityRules = [];
    this.rewards = [];
    this.member = new MemberStore();
    this.cssStyles = "";

    this.view = "launcher";
    this.nav = "store-rewards";
    this.el = null;
    this.memberExternalId = null;
    this.onReadyCallback = null;
    this.error = "";
  }

  getDeserializeMappers() {
    return [
      {"field": "id", "accessor": "id"},
      {"field": "platform", "accessor": "platform"},
      {"field": "widgetConfig", "accessor": "widget_config", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": WidgetConfigStore},
      {"field": "activityRules", "accessor": "activity_rules", "type": constants.DESERIALIZE_TYPE_ARRAY_OBJECTS, "cls": RuleStore},
      {"field": "rewards", "accessor": "rewards", "type": constants.DESERIALIZE_TYPE_ARRAY_OBJECTS, "cls": RewardStore},
      {"field": "member", "accessor": "member", "type": constants.DESERIALIZE_TYPE_OBJECT, "cls": MemberStore},
      {"field": "cssStyles", "accessor": "css_styles"},
    ];
  }

  @action async deserialize(data) {
    await deserializeWithMappers(this, this.getDeserializeMappers(), data);
    await this.updateRulesAvailablility();
  }

  @computed get params() {
    const data = {};
    if (this.memberExternalId) data['member_external_id'] = this.memberExternalId;
    return data;
  }

  @action setFields(data) {
    let self = this;
    for (var key in data) {
      let value = data[key];
      self[key] = value;
    }
  }

  @computed get displayError() {
    return this.error ? true : false;
  }

  @action resetError() {
    this.error = "";
  }

  @computed get selectorPath() {
    return `.ov-app-reperks-widget[data-app-id="${this.id}"]`;
  }

  @computed get selector() {
    return document.querySelector(this.selectorPath);
  }

  @computed get display() {
    if (!utils.isProduction()) return true;

    const member = this.member;
    if (!this.id) return false;
    if (!utils.isEligiblePage()) return false;
    if (member.externalId && !member.isEnrolled) return false;
    return true;
  }

  @computed get isLoggedIn() {
    if (this.memberExternalId) return true;
    if (this.member.externalId) return true;
    return false;
  }

  @action async updateRulesAvailablility() {
    let rules = [];
    let unavailableRules = [];
    let member = this.member;

    // sort first
    this.activityRules.sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1);
    this.activityRules.forEach((rule) => {
      if (member.completedActivityRuleIds.includes(rule.id)) {
        rule.setAvailable(false);
        unavailableRules.push(rule);
        return;
      }

      rule.setAvailable(true);
      rules.push(rule);
    })

    unavailableRules.forEach((rule) => {
      rules.push(rule);
    })

    this.activityRules = rules;
  }

  @computed get isLauncherView() {
    return this.view === "launcher";
  }

  @computed get isPanelView() {
    return this.view === "panel";
  }

  @action switchLauncher() {
    this.view = "launcher";
  }

  @action switchPanel() {
    this.view = "panel";
  }

  @computed get isNavStoreRewards() {
    return this.nav === this.NAV_STORE_REWARDS;
  }

  @computed get isNavEarningPoints() {
    return this.nav === this.NAV_EARNING_POINTS;
  }

  @computed get isNavActivities() {
    return this.nav === this.NAV_ACTIVITIES;
  }

  @action switchNav(nav) {
    let eligibleNavs = [
      this.NAV_STORE_REWARDS,
      this.NAV_EARNING_POINTS,
      this.NAV_ACTIVITIES,
    ];

    if (!eligibleNavs.includes(nav)) return;
    this.nav = nav;
  }

  @action switchNavStoreRewards() {
    this.switchPanel();
    this.nav = this.NAV_STORE_REWARDS;
  }

  @action switchNavEarningPoints() {
    this.switchPanel();
    this.nav = this.NAV_EARNING_POINTS;
  }

  @action switchNavActivities() {
    this.switchPanel();
    this.nav = this.NAV_ACTIVITIES;
  }

  @computed get isShopify() {
    return this.platform === "shopify";
  }

  @computed get isBigCommerce() {
    return this.platform === "bigcommerce";
  }

  @computed get isEasyStore() {
    return this.platform === "easystore";
  }

  @computed get isShopline() {
    return this.platform === "shopline";
  }

  @computed get isShopware() {
    return this.platform === "shopware";
  }

  @computed get isWix() {
    return this.platform === "wix";
  }

  @computed get isWooCommerce() {
    return this.platform === "woocommerce";
  }

  @computed get isOtherPlatforms() {
    return this.platform === "others";
  }

  @computed get loginUrl() {
    if (this.isShopify) return "/account/login";
    if (this.isEasyStore) return "/account/login";
    if (this.isBigCommerce) return "/login.php";
    if (this.isShopline) return "/user/signIn";
    return "";
  }

  @computed get signupUrl() {
    if (this.isShopify) return "/account/register";
    if (this.isEasyStore) return "/account/register";
    if (this.isBigCommerce) return "/login.php?action=create_account";
    if (this.isShopline) return "/user/signUp";
    return "";
  }
}


const pageStore = new PageStore();
export default pageStore;
export { PageStore };
